import React from 'react';
import { Button, TextInput } from '@parsina/framework';
import { C, soft } from '../../constants/theme.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { type } from '../../constants/typography.js';
import { useGiftCodes, notifyCreditsChanged } from '../../hooks/useCredits.js';
import { GiftIcon, CheckIcon } from './CreditIcons.jsx';

/**
 * کد هدیه — redeem a campaign code, and see the ones already spent.
 *
 * THE CODE FIELD IS FORCED LTR even under a Persian layout. The code is a Latin
 * token whose left-to-right order carries meaning, and in an RTL input a
 * leading '-' or a mixed digit run REORDERS ON SCREEN while the value stays
 * correct — so the dentist proofreads a string that is not the one being sent.
 *
 * Persian digits are deliberately NOT converted here. The marketing service
 * normalises them (app/domain/gift_codes.py:normalize_code), so a dentist typing
 * on a Persian keyboard is already handled, and a second normaliser in the
 * browser would be a second place for that rule to drift.
 */

const STATUS_TONE = {
  issued: { color: C.teal, bg: soft(C.teal, 0.12) },
  redeemed: { color: C.navy, bg: soft(C.navy, 0.1) },
  revoked: { color: C.textMuted, bg: C.bg },
  expired: { color: C.textMuted, bg: C.bg },
};

export default function GiftCodeCard({ onRedeemed, compact = false }) {
  const { t, formatNumber } = useTranslation();
  const { codes, redeem } = useGiftCodes();
  const [value, setValue] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    const result = await redeem(value);
    setBusy(false);

    if (result.ok) {
      setDone(result);
      setValue('');
      // The balance in the hero and in the KPI strip is now stale. They both
      // read one number, so every mounted reader refetches rather than this
      // card holding a second copy of it.
      notifyCreditsChanged();
      if (onRedeemed) onRedeemed(result);
      return;
    }
    setError(
      result.code === 'EMPTY'
        ? t('credits.gift.empty')
        : result.message || t('credits.gift.notFound'),
    );
  };

  const SHELL = {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: 18,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  };

  if (done) {
    return (
      <div data-testid="gift-success" style={{ ...SHELL, border: `1.5px solid ${C.green}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            width: 38, height: 38, borderRadius: '50%', background: C.green, color: '#fff',
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}
          >
            <CheckIcon />
          </span>
          <div>
            <div style={{ ...type.body, fontWeight: 800, color: C.textPrimary }}>
              {t('credits.gift.success', { parseh: formatNumber(done.granted) })}
            </div>
            <div style={{ ...type.caption, color: C.textSecondary, marginTop: 3 }}>
              {t('credits.gift.successSub', { visits: formatNumber(done.smartVisits) })}
            </div>
          </div>
        </div>
        {done.balance != null && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ ...type.caption, color: C.textSecondary }}>
              {t('credits.gift.newBalance')}
            </span>
            <span style={{ ...type.body, fontWeight: 800, color: C.teal }}>
              {formatNumber(done.balance)} {t('credits.unit')}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={SHELL} data-testid="gift-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 34, height: 34, borderRadius: 10, background: soft(C.navy, 0.1), color: C.navy,
          display: 'grid', placeItems: 'center',
        }}
        >
          <GiftIcon size={18} />
        </span>
        <div style={{ ...type.title, color: C.textPrimary }}>{t('credits.gift.title')}</div>
      </div>

      {!compact && (
        <div style={{ ...type.caption, color: C.textSecondary, lineHeight: 1.9 }}>
          {t('credits.gift.hint')}
        </div>
      )}

      {/* STACKED, not a row. This card sits in a ~280px rail on the desktop
          surface, and a side-by-side input+button cannot fit there: a flex item
          has `min-width: auto`, so the field refuses to shrink below the
          intrinsic width of its placeholder and pushes the button clean out of
          the card. Stacking fits every width and gives the button a full-width
          target rather than a sliver. */}
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <TextInput
          dir="ltr"
          value={value}
          // The kit hands the EVENT through to the real <input>, so this is
          // `e.target.value` and not a bare string — unlike MoneyInput, which
          // reports the stripped value directly.
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError('');
          }}
          placeholder={t('credits.gift.placeholder')}
          aria-label={t('credits.gift.title')}
          aria-invalid={!!error || undefined}
          // `error` on the kit input paints the red border AND announces the
          // message; the block below it is what a screen reader reaches through
          // role="alert".
          error={error || undefined}
          data-testid="gift-code-input"
          inputStyle={{ fontWeight: 700, letterSpacing: 1.5, textAlign: 'center' }}
        />
        <Button type="submit" disabled={busy} data-testid="gift-code-submit" className="w-full min-h-11">
          {busy ? t('credits.gift.submitting') : t('credits.gift.submit')}
        </Button>
      </form>

      {/* The kit paints the message under the field; this is the LIVE region
          that announces it, which the field's own helper text is not. */}
      {error && (
        <div role="alert" aria-live="polite" data-testid="gift-code-error" className="sr-only">
          {error}
        </div>
      )}

      {!error && !compact && (
        <div style={{ ...type.caption, color: C.textMuted }}>{t('credits.gift.oneUse')}</div>
      )}

      {codes.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ ...type.caption, fontWeight: 700, color: C.textSecondary }}>
            {t('credits.gift.myCodes')}
          </div>
          {codes.map((code) => {
            const tone = STATUS_TONE[code.status] || STATUS_TONE.revoked;
            return (
              <div key={code.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span dir="ltr" style={{ ...type.caption, fontWeight: 800, color: C.navy, letterSpacing: 1, unicodeBidi: 'isolate' }}>
                    {code.code}
                  </span>
                  <span style={{ ...type.caption, color: C.textMuted }}>
                    {t('credits.gift.worth', {
                      parseh: formatNumber(code.parseh),
                      visits: formatNumber(code.smartVisits),
                    })}
                  </span>
                </div>
                <span style={{
                  ...type.caption,
                  fontWeight: 700,
                  color: tone.color,
                  background: tone.bg,
                  borderRadius: 999,
                  padding: '3px 10px',
                  whiteSpace: 'nowrap',
                }}
                >
                  {t(`credits.gift.status.${code.status}`)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
